import React, { useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  useTheme,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Work as CasesIcon,
  AddCircle as AddCircleIcon,
  PersonAdd as PersonAddIcon,
  PersonSearch as PersonSearchIcon,
} from "@mui/icons-material";
import { isAdmin } from "../../api/base";
import { viewportContext } from "../../context/ViewportContext";
import { tokens } from "../../theme/tokens";
import { HEADER_HEIGHT } from "./Header";

const DRAWER_WIDTH = "16vw";
const DRAWER_WIDTH_COLLAPSED = "6vw";

const drawerTransition = (theme: any) =>
  theme.transitions.create("width", {
    easing: tokens.motion.easing,
    duration: tokens.motion.slow,
  });

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  transition: drawerTransition(theme),
  "& .MuiDrawer-paper": {
    width: DRAWER_WIDTH,
    top: HEADER_HEIGHT,
    height: `calc(100% - ${HEADER_HEIGHT})`,
    boxSizing: "border-box",
    backgroundColor: tokens.color.brand.primaryAction,
    borderRight: "none",
    overflowX: "hidden",
    transition: drawerTransition(theme),
  },
}));

const getMenuItems = (isAdminUser: boolean) => [
  { text: "Dashboard", icon: <DashboardIcon />, path: "/app/Dashboard", visible: true },
  { text: "Cases", icon: <CasesIcon />, path: "/app/cases", visible: true },
  { text: "Create a Case", icon: <AddCircleIcon />, path: "/app/newCase", visible: true },
  { text: "Clients", icon: <PersonAddIcon />, path: "/app/addClient", visible: isAdminUser },
  { text: "Field Resource", icon: <PersonSearchIcon />, path: "/app/field-resource", visible: isAdmin() },
];

interface SidebarProps {
  drawerOpen: boolean;
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
  handleDrawerToggle: () => void;
}

export default function Sidebar({
  drawerOpen,
  mobileOpen,
  setMobileOpen,
  handleDrawerToggle,
}: SidebarProps) {
  const theme = useTheme();
  const { tallScreen } = useContext(viewportContext) as { tallScreen?: boolean };
  const location = useLocation();

  const isSelected = (path: string) => location.pathname.includes(path);

  const labelStyles: React.CSSProperties = {
    opacity: drawerOpen || tallScreen ? 1 : 0,
    maxWidth: drawerOpen || tallScreen ? "200px" : "0px",
    transition: theme.transitions.create(["opacity", "max-width"], {
      easing: tokens.motion.easing,
      duration: tokens.motion.fast,
    }),
    whiteSpace: "nowrap",
    overflow: "hidden",
  };

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        padding: `${tokens.space[3]} ${tokens.space[2]}`,
        backgroundColor: tokens.color.brand.primaryAction,
      }}
    >
      <List sx={{ py: 0, display: "flex", flexDirection: "column", gap: tokens.space[1] }}>
        {getMenuItems(isAdmin()).map(
          (item) =>
            item.visible && (
              <ListItem key={item.path} disablePadding sx={{ display: "block" }}>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  selected={isSelected(item.path)}
                  onClick={() => tallScreen && setMobileOpen(false)}
                  sx={{
                    minHeight: 44,
                    borderRadius: tokens.radius.sm,
                    px: 1.5,
                    justifyContent: drawerOpen || tallScreen ? "flex-start" : "center",
                    transition: `background-color ${tokens.motion.fast} ${tokens.motion.easing}, color ${tokens.motion.fast} ${tokens.motion.easing}`,
                    "&:hover": { backgroundColor: tokens.color.brand.primaryActionHover },
                    "&:focus-visible": {
                      outline: `${tokens.focus.width} solid ${tokens.color.brand.onPrimary}`,
                      outlineOffset: `-${tokens.focus.width}`,
                    },
                    "&.Mui-selected": {
                      backgroundColor: tokens.color.surface.default,
                      "&:hover": { backgroundColor: tokens.color.surface.default },
                      "& .MuiListItemIcon-root": { color: tokens.color.brand.primaryAction },
                      "& .MuiListItemText-primary": {
                        color: tokens.color.brand.primaryAction,
                        fontWeight: tokens.type.denseStrong.weight,
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: drawerOpen || tallScreen ? 2 : 0,
                      justifyContent: "center",
                      color: tokens.color.brand.onPrimary,
                      "& svg": { fontSize: tokens.icon.default },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: tokens.type.dense.size,
                      fontWeight: tokens.type.dense.weight,
                      color: tokens.color.brand.onPrimary,
                    }}
                    sx={labelStyles}
                  />
                </ListItemButton>
              </ListItem>
            ),
        )}
      </List>
    </Box>
  );

  if (tallScreen) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            top: HEADER_HEIGHT,
            height: `calc(100% - ${HEADER_HEIGHT})`,
            backgroundColor: tokens.color.brand.primaryAction,
            borderRight: "none",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <StyledDrawer
      variant="permanent"
      open={drawerOpen}
      sx={{
        width: drawerOpen ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED,
        "& .MuiDrawer-paper": {
          width: drawerOpen ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED,
        },
      }}
    >
      {drawerContent}
    </StyledDrawer>
  );
}
